import React, { useState } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { SectionHeading } from '../components/ui/SectionHeading';
import {
  MousePointerClick,
  Layers,
  Globe2,
  ShieldCheck,
  Search,
  Zap,
  Smartphone,
  Anchor,
  FileText,
  Lock,
  Server,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Palette,
  BarChart3,
  Sliders,
} from 'lucide-react';

export const FeaturesPage: React.FC = () => {
  const { navigate } = useNavigation();
  const [activeTab, setActiveTab] = useState<'BUILDER' | 'SECTORS' | 'DOMAIN' | 'SEO' | 'SECURITY'>('BUILDER');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <SectionHeading
          badge="Platform Özellikleri"
          title="Modern Bir Web Sitesi İçin Eksiksiz Güç"
          subtitle="Tek satır kod yazmadan sektörünüzün en prestijli web sitesini kurun, özelleştirin ve hızla büyütün."
        />

        {/* Feature Category Tabs */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-2 p-1.5 rounded-2xl bg-slate-900 border border-slate-800 max-w-3xl mx-auto">
          <button
            onClick={() => setActiveTab('BUILDER')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
              activeTab === 'BUILDER' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            Görsel Editör
          </button>
          <button
            onClick={() => setActiveTab('SECTORS')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
              activeTab === 'SECTORS' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            Sektörel Modüller
          </button>
          <button
            onClick={() => setActiveTab('DOMAIN')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
              activeTab === 'DOMAIN' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            Domain & SSL
          </button>
          <button
            onClick={() => setActiveTab('SEO')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
              activeTab === 'SEO' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            SEO & Hız
          </button>
          <button
            onClick={() => setActiveTab('SECURITY')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
              activeTab === 'SECURITY' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            Güvenlik & Bulut
          </button>
        </div>

        {/* Dynamic Feature Content Section */}
        <div className="mt-12 bg-slate-900/80 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl">
          {activeTab === 'BUILDER' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-600/10 text-blue-400 border border-blue-500/20 flex items-center justify-center">
                  <MousePointerClick className="w-6 h-6" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  Tasarımcıya İhtiyaç Duymadan Canlı Görsel Düzenleme
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Adiosoft’un sezgisel görsel editörü, sitenizin her köşesini tıklayıp anında düzenlemenize imkan tanır. Tipografi stillerini, renk şemalarını ve blok sıralarını canlı olarak izleyin.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Metin ve görsel bloklarını doğrudan tıklayarak düzenleyin</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Masaüstü, tablet ve mobil görünümlerini anlık simüle edin</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Geri al / İleri al ve otomatik taslak kaydetme sistemi</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-[16/10] bg-slate-950 p-4 flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1000&auto=format&fit=crop"
                  alt="Editor Showcase"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>
          )}

          {activeTab === 'SECTORS' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <div className="w-12 h-12 rounded-2xl bg-amber-600/10 text-amber-400 border border-amber-500/20 flex items-center justify-center">
                  <Anchor className="w-6 h-6" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  Sektörünüzün İş Süreçlerine Tam Uyumlu Dahili Modüller
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Yat kiralama filosu yönetiminden kurumsal blog ve vaka çalışmalarına kadar, üçüncü taraf eklenti kurmadan tüm sektörel araçlar hazır gelir.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Yat özellikleri, kabin sayısı, uzunluk ve sezonluk fiyat matrisi</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Müşteri rezervasyon talepleri ve WhatsApp anlık bildirim kanalı</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Kurumsal haber ve blog yayınlama motoru</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-[16/10] bg-slate-950 p-4 flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?q=80&w=1000&auto=format&fit=crop"
                  alt="Yacht and Sector Module"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>
          )}

          {activeTab === 'DOMAIN' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <div className="w-12 h-12 rounded-2xl bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center">
                  <Globe2 className="w-6 h-6" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  Kendi Alan Adınızı Bağlayın — DNS & SSL Otomatik
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  İster yeni bir domain bağlayın, ister mevcut .com / .com.tr alan adınızı A ve CNAME kayıtlarıyla yönlendirin. Akıllı doğrulayıcımız DNS yayılımını otomatik takip eder.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Ücretsiz 256-bit SSL sertifikası (Let's Encrypt)</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Otomatik HTTPS zorlaması ve HTTP/3 desteği</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Adiosoft ücretsiz adiosoft.com/site-adi web adresi desteği</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-[16/10] bg-slate-950 p-6 flex flex-col justify-center font-mono text-xs text-slate-300 space-y-4">
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between">
                  <span className="text-slate-400">DNS Durumu:</span>
                  <span className="text-emerald-400 font-bold">Bulut Altyapısı ile Doğrulandı</span>
                </div>
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between">
                  <span className="text-slate-400">SSL Sertifikası:</span>
                  <span className="text-blue-400 font-bold">Aktif & Geçerli (256-Bit TLS 1.3)</span>
                </div>
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between">
                  <span className="text-slate-400">Global Edge CDN:</span>
                  <span className="text-indigo-400 font-bold">İstanbul & Frankfurt PoP Noktaları</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'SEO' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <div className="w-12 h-12 rounded-2xl bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  Google’da İlk Sayfaya Çıkaran Dahili SEO Motoru
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Otomatik meta etiketleri, Google SERP simülatörü, temiz URL yapısı ve OpenGraph sosyal medya kartları ile arama motorlarında rakiplerinizin önüne geçin.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Otomatik sitemap.xml ve robots.txt üretimi</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Google PageSpeed 95+ Core Web Vitals optimizasyonu</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>WhatsApp ve sosyal paylaşımlarda şık zengin kartlar</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-[16/10] bg-slate-950 p-6 flex flex-col justify-center text-xs space-y-3">
                <div className="text-slate-400 font-mono text-[11px]">Google Arama Önizlemesi (SERP)</div>
                <div className="p-4 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                  <div className="text-blue-400 font-semibold text-sm hover:underline cursor-pointer">
                    Lüks Bodrum & Göcek Yat Kiralama | Apex Yachting
                  </div>
                  <div className="text-emerald-400 font-mono text-[11px]">
                    https://www.apexyachting.com/gulet-ve-motoryat
                  </div>
                  <div className="text-slate-400 text-xs line-clamp-2">
                    Bodrum ve Fethiye çıkışlı VIP gulet ve motoryat kiralama. En uygun haftalık fiyatlar ve 7/24 profesyonel kaptanlı hizmet.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'SECURITY' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <div className="w-12 h-12 rounded-2xl bg-purple-600/10 text-purple-400 border border-purple-500/20 flex items-center justify-center">
                  <Lock className="w-6 h-6" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  Kurumsal Güvenlik ve Tam Veri İzolasyonu
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Multi-tenant mimarimiz her müşterinin verisini, medya varlıklarını ve rezervasyonlarını bağımsız ve şifreli bir katmanda saklar.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Site bazlı tenant kimlik doğrulaması ve veri izolasyonu</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Günlük otomatik bulut yedekleme ve tek tıkla geri yükleme</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>DDoS koruması ve %99.9 kesintisiz çalışma (SLA)</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-[16/10] bg-slate-950 p-6 flex flex-col justify-center space-y-4">
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-300 font-semibold">Tenant İzolasyon Katmanı</span>
                  <span className="text-emerald-400 font-mono">100% Güvenli</span>
                </div>
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-300 font-semibold">Uptime Oranı</span>
                  <span className="text-blue-400 font-mono">%99.98</span>
                </div>
                <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-300 font-semibold">Yedekleme Sıklığı</span>
                  <span className="text-indigo-400 font-mono">Her 24 Saatte Bir</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comprehensive 6-Box Grid */}
        <div className="mt-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="text-2xl font-bold text-white mb-2">Tüm Teknik İmkanlar</h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Gelişmiş mimarimizin arkasındaki güç.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
              <Palette className="w-6 h-6 text-blue-400 mb-3" />
              <h4 className="text-base font-bold text-white mb-1">Özel Renk & Font Yönetimi</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Google Fonts kütüphanesinden dilediğiniz fontu seçin, kurumsal renk kodlarınızı tek tıkla tüm siteye uygulayın.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
              <FileText className="w-6 h-6 text-indigo-400 mb-3" />
              <h4 className="text-base font-bold text-white mb-1">Medya & Galeri Kütüphanesi</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Yüksek çözünürlüklü fotoğrafları yükleyin; otomatik WebP formatına dönüştürerek sitenizin hızını koruyun.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
              <BarChart3 className="w-6 h-6 text-emerald-400 mb-3" />
              <h4 className="text-base font-bold text-white mb-1">Ziyaretçi & Form İstatistikleri</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Hangi sayfanın kaç kez görüntülendiğini ve gelen rezervasyon formlarını anlık grafiklerle takip edin.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
              <Smartphone className="w-6 h-6 text-sky-400 mb-3" />
              <h4 className="text-base font-bold text-white mb-1">WhatsApp Hızlı İletişim Butonu</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Müşterilerinizin tek tıkla doğrudan şirket WhatsApp hattınıza bağlanmasını sağlayan akıllı widget.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
              <Sliders className="w-6 h-6 text-amber-400 mb-3" />
              <h4 className="text-base font-bold text-white mb-1">Sektörel Özel Alanlar</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Yat boyu, motor gücü, kabin adedi veya avukatlık uzmanlık alanları gibi sektöre özel dinamik veri alanları.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
              <Server className="w-6 h-6 text-purple-400 mb-3" />
              <h4 className="text-base font-bold text-white mb-1">Yüksek Kapasiteli Global CDN</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Siteniz dünyanın neresinden ziyaret edilirse edilsin en yakın sunucudan yıldırım hızında yüklenir.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <button
            onClick={() => navigate('/kayit')}
            className="px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-xl shadow-blue-600/30 transition"
          >
            Hemen Ücretsiz Deneyin
          </button>
        </div>
      </div>
    </div>
  );
};
